import { format } from "date-fns"
import { ru } from "date-fns/locale/ru"
import type { AudienceFilter } from "../api/audience-api"

/** Условия, которые приходят из других экранов: метка, блок воронки, рассылка, период. */
export const CONTEXT_KEYS = [
	"search",
	"state",
	"tagId",
	"entryLink",
	"passedNodeId",
	"stuckNodeId",
	"joinedAfter",
	"activeAfter",
	"failedInBroadcastId",
	"excludeBroadcastId",
	"inactiveDays",
	"payments",
] as const

export type ContextKey = (typeof CONTEXT_KEYS)[number]

const STATE_TEXT = {
	waiting: "ждут ответа бота",
	idle: "закончили сценарий",
	blocked: "заблокировали бота",
} as const

const moment = (value: string) => format(new Date(value), "d MMM, HH:mm", { locale: ru })

/** Эти условия словами, по одному на условие. */
export function contextChips(
	filter: AudienceFilter,
	tags: { id: string; name: string }[] = [],
): { key: ContextKey; label: string }[] {
	const chips: { key: ContextKey; label: string }[] = []
	const push = (key: ContextKey, label: string) => chips.push({ key, label })
	if (filter.search) push("search", `в имени или ответах «${filter.search}»`)
	if (filter.state) push("state", STATE_TEXT[filter.state])
	if (filter.tagId)
		push("tagId", `в группе «${tags.find((tag) => tag.id === filter.tagId)?.name ?? "удалённая"}»`)
	if (filter.entryLink) push("entryLink", `пришли по метке ${filter.entryLink}`)
	if (filter.passedNodeId) push("passedNodeId", "прошли выбранный блок воронки")
	if (filter.stuckNodeId) push("stuckNodeId", "стоят на выбранном блоке воронки")
	if (filter.joinedAfter) push("joinedAfter", `пришли после ${moment(filter.joinedAfter)}`)
	if (filter.activeAfter) push("activeAfter", `писали после ${moment(filter.activeAfter)}`)
	if (filter.failedInBroadcastId)
		push("failedInBroadcastId", "прошлая рассылка не дошла из-за ошибки")
	if (filter.excludeBroadcastId) push("excludeBroadcastId", "не получали прошлую рассылку")
	if (filter.inactiveDays) push("inactiveDays", `не писали боту ${filter.inactiveDays} дн.`)
	if (filter.payments)
		push("payments", filter.payments === "has" ? "оплатили в боте" : "не оплачивали в боте")
	return chips
}

/** Только эти условия: чтобы не потерять их, когда меняют остальные. */
export function contextOf(filter: AudienceFilter): Partial<AudienceFilter> {
	return Object.fromEntries(
		CONTEXT_KEYS.flatMap((key) => (filter[key] ? [[key, filter[key]]] : [])),
	) as Partial<AudienceFilter>
}
